import React from 'react';
import { addDays, format, isEqual } from 'date-fns';

// Material-UI
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridColumnHeaderParams,
  GridOverlay
} from '@mui/x-data-grid';

// Styles
import { useTheme } from '@mui/material/styles';

import {
  DateRange,
  RequestMeal
} from '../../models';

import {
  EMPTY_STRING
} from '../../global';

interface MealsGridProps {
  dateRange: DateRange;
  isLoading: boolean;
  nights: number;
  rows: Array<RequestMeal>;
}

const MealsGrid: React.FC<MealsGridProps> = (props) => {

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      '& .super-app-theme--header': {
        backgroundColor: 'rgba(255, 7, 0, 0.55)'
      }
    },
    noRows: {
      margin: theme.spacing(2, 0, 0, 0),
      padding: theme.spacing(2, 0, 2, 0)
    }
  };

  // #endregion

  // #region Methods

  /*
 * Grid column definitions
 */
  const columns = (): Array<GridColDef> => {

    const base: Array<GridColDef> = [
      {
        field: 'category',
        headerName: 'Category',
        headerAlign: 'center',
        width: 175,
        type: 'string',
        renderCell: (params: GridCellParams) => (
          renderCategory(params)
        )
      }
    ];

    // 
    // Data for each date
    const dates: Array<GridColDef> = [];

    for (let i = 0; i < props.nights; i++) {
      dates.push(
        {
          field: `date` + i.toString(),
          headerAlign: 'center',
          renderHeader: (params: GridColumnHeaderParams) => (
            renderHeader(params, i)
          ),
          align: 'right',
          width: 100,
          type: 'string',
          renderCell: (params: GridCellParams) => (
            renderMeal(params, i)
          )
        }
      );
    }

    return [...base, ...dates];

  }

  /*
   * Overlay displayed when no data
   */
  const noRowsOverlay = () => {
    return (
      <GridOverlay>
        <div style={styles.noRows}>No nightly counts for the selected date range</div>
      </GridOverlay>
    );
  }

  /*
 * Render the category for column 1
 */
  const renderCategory = (params: GridCellParams) => {

    return (
      <span>{params.row.category}</span>
    );

  }

  /*
   * Render the header for a date column
   */
  const renderHeader = (params: GridColumnHeaderParams, index: number) => {

    if (props.dateRange.startDate !== EMPTY_STRING && props.dateRange.endDate !== EMPTY_STRING) {
      const start = new Date(props.dateRange.startDate);
      const date = format(addDays(start, index), 'EEEEEE M/d');

      return (
        <span>{date}</span>
      );
    }
  }

  /*
   * Render meal info for row
   */
  const renderMeal = (params: GridCellParams, index: number) => {

    const start = new Date(props.dateRange.startDate);
    const date = addDays(start, index);
    const meals = params.row as RequestMeal;

    const m = meals.mealCounts.find(m => {
      return isEqual(new Date(m.lodgeNight), date);
    });

    if (m !== undefined) {
      return (
        <><span>{m.count}</span></>
      )
    } else {
      return (
        <span>-</span>
      );
    }
  }

  // #endregion

  return (
    <DataGrid
      columns={columns()}
      rows={props.rows}
      getRowId={(row) => row.rowID} // Special unique ID for each row
      sx={styles.root}
      density="compact"
      disableColumnSelector
      disableRowSelectionOnClick
      hideFooter
      loading={props.isLoading}
      slots={{
        noRowsOverlay: noRowsOverlay
      }}
    />
  );

}

export default MealsGrid;